import { createContext, useContext, useState, useEffect } from "react";
import { useRecoilValue } from "recoil";
import { isAuthenticated } from "../store";

const DarkModeContext = createContext();

/**
 * DarkModeProvider component that manages dark mode state globally
 * Enables persistent dark mode using Tailwind's `dark` class strategy
 */
export function DarkModeProvider({ children }) {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const userAuthenticated = useRecoilValue(isAuthenticated);

  // Initialize dark mode from saved preference or system setting
  useEffect(() => {
    try {
      const saved = localStorage.getItem("darkMode");
      if (saved !== null) {
        setIsDarkMode(saved === "true");
      } else if (
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
      ) {
        setIsDarkMode(true);
      } else {
        setIsDarkMode(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Apply Tailwind `dark` class to the root element and persist preference
  useEffect(() => {
    if (isLoading) return;
    const htmlElement = document.documentElement;
    if (isDarkMode) {
      htmlElement.classList.add("dark");
    } else {
      htmlElement.classList.remove("dark");
    }
    try {
      localStorage.setItem("darkMode", String(isDarkMode));
    } catch (_) {}
  }, [isDarkMode, isLoading]);

  // Allow users to toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  return (
    <DarkModeContext.Provider value={{ isDarkMode, toggleDarkMode, isLoading }}>
      {children}
    </DarkModeContext.Provider>
  );
}

/**
 * Hook to use dark mode context
 * @returns {Object} { isDarkMode, toggleDarkMode, isLoading }
 */
export function useDarkMode() {
  const context = useContext(DarkModeContext);

  if (!context) {
    throw new Error("useDarkMode must be used within DarkModeProvider");
  }

  return context;
}
